import React, { useEffect, useMemo, useState } from "react"
import { Alert, Box, Button } from "@mui/material"
import { MaterialReactTable } from "material-react-table"
import api from "../../../../base/services/ResApi"
import TerminalAssignmentModal from "./TerminalAssignmentModal"


export default function TerminalRegistrationView({ query, unassigned, refresh, onRefresh }) {
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 })
  const [selection, setSelection] = useState({})
  const [assignment, setAssignment] = useState(null)
  const [revision, setRevision] = useState(0)
  const columns = useMemo(() => [
    { accessorKey: "serialNo", header: "Terminal serial", size: 190 },
    { accessorKey: "terminalId", header: "Terminal ID", size: 130 },
    { accessorKey: "model", header: "Model", size: 130 },
    { accessorKey: "orgName", header: "Organization", size: 220 },
    { accessorKey: "orgCode", header: "Organization code", size: 170 },
    ...(!unassigned ? [{ accessorKey: "groupName", header: "Group", size: 180 }] : []),
    { accessorKey: "status", header: "Status", size: 150 },
    { accessorKey: "createdDateTime", header: "Created", size: 220 },
    { accessorKey: "updatedDateTime", header: "Last updated", size: 220 },
  ], [unassigned])

  useEffect(() => {
    if (refresh) { setRevision(value => value + 1); onRefresh(false) }
  }, [refresh, onRefresh])

  useEffect(() => {
    if (!query) return
    let current = true
    setLoading(true)
    setError("")
    setSelection({})
    api.get("/terminals", {
      headers: {
        Action: unassigned ? "V_ALL_UNASSIGNED_TMNL" : "V_ALL_PENDING_TMNL",
        Filter: unassigned ? "F_ORG_CODE" : query.filterType,
        Value: query.value,
        "Sort-Column": "serial_no", "Sort-Order": "asc",
        "Page-Number": pagination.pageIndex + 1,
        "Page-Size": pagination.pageSize, "Is-Pagination": "true",
      }
    }).then(response => {
      if (!current) return
      if (Number(response.data?.code) >= 400) throw new Error(response.data?.description || "Unable to load terminals.")
      setData(response.data?.dataList || [])
      setTotal(response.data?.total || 0)
    }).catch(err => {
      if (!current) return
      setData([])
      setTotal(0)
      setError(err.response?.data?.description || err.message || "Unable to load terminals. Please try again.")
    }).finally(() => {
      if (current) setLoading(false)
    })
    return () => { current = false }
  }, [query, unassigned, pagination.pageIndex, pagination.pageSize, revision])

  return <Box sx={{ mt: 2 }}>
    {query && <Box sx={{ mb: 1, color: "text.secondary", fontSize: 14 }}>
      Results for {query.filterType === "F_ORG_CODE" ? "organization" : query.filterType === "F_GRP_ID" ? "group" : "terminal serial"}: {query.value}
    </Box>}
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    <MaterialReactTable columns={columns} data={data} manualPagination rowCount={total}
      state={{ isLoading: loading, pagination, rowSelection: selection }}
      onPaginationChange={setPagination} onRowSelectionChange={setSelection}
      getRowId={row => String(row.terminalId ?? `${row.orgCode}:${row.serialNo}`)}
      enableRowSelection={unassigned ? row => row.original.terminalId != null : false}
      enableSorting={false} enableColumnFilters={false} enableGlobalFilter={false}
      renderTopToolbarCustomActions={({ table }) => <Box sx={{ display: "flex", gap: 1 }}>
        <Button disabled={!query || loading} onClick={() => setRevision(value => value + 1)}>Refresh</Button>
        {unassigned && <Button disabled={loading || !table.getSelectedRowModel().rows.length}
          onClick={() => setAssignment(table.getSelectedRowModel().rows.map(row => row.original))}>Assign to group</Button>}
      </Box>}
      renderEmptyRowsFallback={() => <Box sx={{ p: 4, textAlign: "center" }}>
        {!query ? "Choose your filters and click Search." : loading ? "Loading terminals…" : unassigned ? "No unassigned terminals found." : "No pending bulk registrations found."}
      </Box>} />
    {assignment && <TerminalAssignmentModal terminals={assignment} onClose={() => setAssignment(null)}
      onSaved={() => { setAssignment(null); setSelection({}); setRevision(value => value + 1) }} />}
  </Box>
}
